import { Component, OnInit, OnDestroy } from '@angular/core';
import { BcPrSelectionComponent } from './bc-pr-selection/bc-pr-selection.component';
import { BcPrResultTableComponent } from './bc-pr-result-table/bc-pr-result-table.component';
import { SharedServiceService } from '../../services/shared-service.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-bc-prediction-results',
  imports: [BcPrSelectionComponent, BcPrResultTableComponent],
  templateUrl: './bc-prediction-results.component.html',
  styleUrl: './bc-prediction-results.component.scss'
})

/**
 * Right-side prediction panel.
 * Converts backend prediction payloads into fixed table slots for Blend 1 and Blend 2.
 * Supports both payload styles currently returned by backend:
 * - Explicit pred_num per row.
 * - Flat array split by fixed property count.
 */
export class BcPredictionResultsComponent implements OnInit, OnDestroy {
  j300_1Details: string[] = [];
  j300_2Details: string[] = [];

  // Prediction data
  blend1Predictions: string[] = [];
  blend2Predictions: string[] = [];

  private predictionSubscription?: Subscription;

  constructor(private sharedService: SharedServiceService) { }

  ngOnInit(): void {
    // Subscribe to the prediction data
    this.predictionSubscription = this.sharedService.prediction_results$.subscribe({
      next: (predictionsData) => {

        if (predictionsData && Array.isArray(predictionsData) && predictionsData.length > 0) {
          this.mapPredictionsToTable(predictionsData);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.predictionSubscription?.unsubscribe();
  }

  /**
  * Maps backend prediction data to the table format
  * Separates Blend 1 (Prediction 1) and Blend 2 (Prediction 2) data
  * @param predictions Array that can contain multiple predictions
   */
  mapPredictionsToTable(predictions: any[]): void {
    // Expected order in the table (according to bc-pr-result-table.component.ts)
    const propertyOrder = [
      'KV100', 'KV40', 'VI', 'KV20', 'HTHS 150C', 'HTHS 100C',
      'KV100 (30 Cycles Sheared)', 'KV100 (90 Cycles Sheared)', 'CCS',
      '-10C (25W)', '-15C (20W)', '-20C (15W)', '-25C (10W)', '-30C (5W)', '-35C (0W)',
      'NOACK', 'BOBV (100C)', 'BOBV (40C)', 'BOB VI', 'BOB Saturates', 'BOB Sulfur'
    ];

    // The backend can return:
    // Option 1: Single array with {Property, Prediction, pred_num} where pred_num indicates the blend
    // Option 2: Flat array where the first 21 items are pred1 and the next 21 are pred2

    // Separate predictions by pred_num or by position
    const pred1Map = new Map<string, any>();
    const pred2Map = new Map<string, any>();

    const normalizePropertyName = (property: any): string => {
      const value = String(property ?? '').trim();
      const aliases: Record<string, string> = {
        '30 Cycles Sheared Viscosity': 'KV100 (30 Cycles Sheared)',
        '90 Cycles Sheared Viscosity': 'KV100 (90 Cycles Sheared)',
        'KV100 (30 Cycles Sheared)': 'KV100 (30 Cycles Sheared)',
        'KV100 (90 Cycles Sheared)': 'KV100 (90 Cycles Sheared)'
      };
      return aliases[value] || value;
    };

    // Verify whether predictions have a pred_num field to identify the blend
    const hasPredNum = predictions.some(p => p.pred_num !== undefined);

    if (hasPredNum) {
      // Case 1: Each item has pred_num
      predictions.forEach(pred => {
        const predNum = pred.pred_num || 1;
        const map = predNum === 1 ? pred1Map : pred2Map;
        const normalizedProperty = normalizePropertyName(pred.Property);
        map.set(normalizedProperty, pred.Prediction);
      });
    } else {
      // Case 2: Split by halves (first 21 items = pred1, next 21 = pred2)
      const itemsPerPrediction = propertyOrder.length; // 21

      if (predictions.length === itemsPerPrediction) {
        // There is only one blend of data - put everything in pred1
        predictions.forEach(pred => {
          pred1Map.set(pred.Property, pred.Prediction);
        });
      } else if (predictions.length === itemsPerPrediction * 2) {
        // There are two blends - split by halves

        predictions.forEach((pred, index) => {
          const map = index < itemsPerPrediction ? pred1Map : pred2Map;
          const normalizedProperty = normalizePropertyName(pred.Property);
          map.set(normalizedProperty, pred.Prediction);
        });
      } else {
        // Try to map whatever is available
        predictions.forEach((pred, index) => {
          const map = index < itemsPerPrediction ? pred1Map : pred2Map;
          map.set(normalizePropertyName(pred.Property), pred.Prediction);
        });
      }
    }

    const decimalByProperty = new Map<string, number>([
      ['VI', 0],
      ['-10C (25W)', 0],
      ['-15C (20W)', 0],
      ['-20C (15W)', 0],
      ['-25C (10W)', 0],
      ['-30C (5W)', 0],
      ['-35C (0W)', 0],
      ['BOB VI', 0],
      ['NOACK', 1],
      ['KV100', 2],
      ['KV40', 2],
      ['KV20', 2],
      ['HTHS 150C', 2],
      ['HTHS 100C', 2],
      ['KV100 (30 Cycles Sheared)', 2],
      ['KV100 (90 Cycles Sheared)', 2],
      ['BOBV (100C)', 2],
      ['BOBV (40C)', 2],
      ['BOB Saturates', 2],
      ['BOB Sulfur', 4]
    ]);

    const truncateNumber = (value: number, decimals: number): number => {
      const factor = Math.pow(10, decimals);
      return Math.trunc(value * factor) / factor;
    };

    const coerceNumeric = (value: any): number | null => {
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
          return null;
        }
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };

    // Helper function to format values by property
    const formatValue = (property: string, value: any): string => {
      if (value === undefined || value === null || value === '') {
        return '';
      }
      if (value === 'NaN') {
        return 'NaN';
      }

      const decimals = decimalByProperty.get(property);
      if (decimals === undefined) {
        return String(value);
      }

      const numericValue = coerceNumeric(value);
      if (numericValue === null) {
        return String(value);
      }

      const truncated = truncateNumber(numericValue, decimals);
      return truncated.toFixed(decimals);
    };

    // Map in the correct order for Blend 1
    this.blend1Predictions = propertyOrder.map(property =>
      formatValue(property, pred1Map.get(property))
    );

    // Map in the correct order for Blend 2
    this.blend2Predictions = propertyOrder.map(property =>
      formatValue(property, pred2Map.get(property))
    );
  }

  onGradeSelected(event: { j300_1: string[], j300_2: string[] }): void {
    this.j300_1Details = event.j300_1;
    this.j300_2Details = event.j300_2;
  }

  /**
  * Clears the Blend 1 prediction data (left column)
   */
  clearBlend1(): void {
    this.blend1Predictions = [];
    this.clearPredictionsIfEmpty();
  }

  /**
  * Clears the Blend 2 prediction data (right column)
   */
  clearBlend2(): void {
    this.blend2Predictions = [];
    this.clearPredictionsIfEmpty();
  }

  private clearPredictionsIfEmpty(): void {
    if (this.blend1Predictions.length === 0 && this.blend2Predictions.length === 0) {
      this.sharedService.set_prediction_results([]);
    }
  }

}
